/**
 * Plays a Web Audio node through a local WebRTC loopback into an <audio>
 * element, so the browser's echo canceller knows about it.
 *
 * Chrome's echo cancellation (the mic's `echoCancellation: true`) only
 * subtracts audio the browser itself plays out through WebRTC; sound sent
 * straight to `AudioContext.destination` leaks back into the mic. On a
 * speakerphone-style call the voice then hears itself, and the caller can't
 * talk over it. Looping the output through a pair of local peer connections
 * makes it WebRTC-rendered audio the canceller removes.
 *
 * Falls back to the plain destination wherever WebRTC is unavailable or the
 * loopback fails; audio still plays, just without the cancellation.
 *
 * @returns a function that tears the loopback down.
 */
export function playEchoSafe(
  context: AudioContext,
  node: AudioNode,
): () => void {
  if (
    typeof RTCPeerConnection === 'undefined' ||
    typeof context.createMediaStreamDestination !== 'function'
  ) {
    node.connect(context.destination);
    return () => undefined;
  }

  const destination = context.createMediaStreamDestination();
  node.connect(destination);
  const sender = new RTCPeerConnection();
  const receiver = new RTCPeerConnection();
  const element = new Audio();
  element.autoplay = true;
  let closed = false;

  const fallBack = () => {
    if (closed) return;
    try {
      node.disconnect(destination);
    } catch {
      // already disconnected
    }
    node.connect(context.destination);
  };

  sender.onicecandidate = ({ candidate }) => {
    if (candidate) void receiver.addIceCandidate(candidate).catch(fallBack);
  };
  receiver.onicecandidate = ({ candidate }) => {
    if (candidate) void sender.addIceCandidate(candidate).catch(fallBack);
  };
  receiver.ontrack = ({ streams }) => {
    element.srcObject = streams[0] ?? null;
    void element.play().catch(fallBack);
  };
  for (const track of destination.stream.getAudioTracks()) {
    sender.addTrack(track, destination.stream);
  }

  void (async () => {
    const offer = await sender.createOffer();
    await sender.setLocalDescription(offer);
    await receiver.setRemoteDescription(offer);
    const answer = await receiver.createAnswer();
    await receiver.setLocalDescription(answer);
    await sender.setRemoteDescription(answer);
  })().catch(fallBack);

  return () => {
    closed = true;
    element.pause();
    element.srcObject = null;
    sender.close();
    receiver.close();
  };
}
